import React, { useEffect, useMemo, useState } from "react";
import { Chess } from "chess.js";
import { api } from "../lib/api";
import Board from "../components/Board";
import { Sounds } from "../lib/sounds";
import { useClickToMove } from "../lib/useClickToMove";
import { Link } from "react-router-dom";
import { Trophy, ArrowRight } from "lucide-react";

/*
 * Adaptive Test My Elo:
 *  - Start estimate 1200. Backend serves one puzzle at a time near the current estimate.
 *  - Each answer updates estimate up (correct) or down (wrong).
 *  - After 8 rounds we submit and receive a warm, encouraging framing + percentile.
 */
const STARTING_ESTIMATE = 1200;

export default function TestElo() {
  const [phase, setPhase] = useState("intro"); // intro|playing|done
  const [current, setCurrent] = useState(null); // { puzzle, step, total, current_estimate }
  const [chess, setChess] = useState(null);
  const [expected, setExpected] = useState([]);
  const [attempted, setAttempted] = useState(false); // has user attempted this puzzle?
  const [flash, setFlash] = useState({});
  const [history, setHistory] = useState([]);
  const [finalRes, setFinalRes] = useState(null);
  const [err, setErr] = useState("");

  const orientation = useMemo(() => {
    if (!current?.puzzle) return "white";
    try {
      const c = new Chess(current.puzzle.fen);
      const opp = current.puzzle.moves[0];
      c.move({ from: opp.slice(0, 2), to: opp.slice(2, 4), promotion: opp[4] });
      return c.turn() === "w" ? "white" : "black";
    } catch {
      return "white";
    }
  }, [current]);

  const setupPuzzle = (pkt) => {
    const p = pkt.puzzle;
    const c = new Chess(p.fen);
    const opp = p.moves[0];
    c.move({ from: opp.slice(0, 2), to: opp.slice(2, 4), promotion: opp[4] });
    setCurrent(pkt);
    setChess(c);
    setExpected([...p.moves.slice(1)]);
    setAttempted(false);
    setFlash({
      [opp.slice(0, 2)]: { background: "rgba(198,161,91,0.3)" },
      [opp.slice(2, 4)]: { background: "rgba(198,161,91,0.5)" },
    });
    Sounds.move();
  };

  const fetchNext = async (nextHistory, estimate, stepIndex) => {
    try {
      const { data } = await api.post("/elo-test/next", {
        current_estimate: estimate,
        step_index: stepIndex,
        history: nextHistory,
      });
      setupPuzzle(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not load next puzzle.");
      setPhase("intro");
    }
  };

  const start = async () => {
    setErr("");
    setHistory([]);
    setFinalRes(null);
    setPhase("playing");
    await fetchNext([], STARTING_ESTIMATE, 0);
  };

  const finalize = async (fullHistory) => {
    try {
      const { data } = await api.post("/elo-test/submit", {
        final_estimate: 0, // server ignores and recomputes from history
        history: fullHistory,
      });
      setFinalRes(data);
      setPhase("done");
      Sounds.correct();
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not submit test.");
    }
  };

  const advance = async (solved) => {
    const entry = {
      puzzle_id: current.puzzle.id,
      solved,
      rating_served: current.puzzle.rating,
    };
    const newHistory = [...history, entry];
    setHistory(newHistory);

    if (current.step >= current.total) {
      // send raw history — server recomputes the final estimate authoritatively
      await finalize(newHistory);
    } else {
      // server computes next estimate from history — we just forward it
      await fetchNext(newHistory, current.current_estimate, current.step);
    }
  };

  const applyMove = (c, uci) => {
    const cc = new Chess(c.fen());
    const legal = cc.moves({ verbose: true }).find((m) => m.from === uci.slice(0, 2) && m.to === uci.slice(2, 4));
    if (!legal) return { chess: cc, isCapture: false };
    cc.move({ from: legal.from, to: legal.to, promotion: legal.promotion || uci[4] || "q" });
    return { chess: cc, isCapture: !!legal.captured };
  };

  const attempt = (from, to, piece, isCapture, promotion) => {
    if (attempted || phase !== "playing") return false;
    const uci = from + to + (promotion || "");
    const expected0 = expected[0];
    const matches =
      uci === expected0 ||
      (uci.length === 4 && expected0.length === 5 && expected0[4] === "q" && uci === expected0.slice(0, 4));
    setAttempted(true);

    if (!matches) {
      Sounds.wrong();
      setFlash({
        [from]: { background: "rgba(160,74,60,0.5)" },
        [to]: { background: "rgba(160,74,60,0.6)" },
      });
      // small pause then advance
      setTimeout(() => advance(false), 900);
      return false;
    }

    // correct — play remaining solution moves then advance
    const { chess: c1, isCapture: cap } = applyMove(chess, expected0);
    if (cap) Sounds.capture(); else Sounds.move();
    setFlash({
      [from]: { background: "rgba(92,135,85,0.4)" },
      [to]: { background: "rgba(92,135,85,0.55)" },
    });
    setChess(c1);
    let rest = expected.slice(1);

    const playRest = async () => {
      let cur = c1;
      for (let i = 0; i < rest.length; i++) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 380));
        const { chess: next, isCapture: cap2 } = applyMove(cur, rest[i]);
        if (cap2) Sounds.capture(); else Sounds.move();
        cur = next;
        setChess(next);
      }
      setTimeout(() => advance(true), 500);
    };
    playRest();
    return true;
  };

  const clickToMove = useClickToMove(chess, attempt, !attempted && phase === "playing");
  const combinedStyles = { ...flash, ...clickToMove.squareStyles };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
      <div className="overline mb-2">Test my Elo</div>
      <h1 className="font-serif text-3xl sm:text-4xl text-[#EFE8D8] mb-6">Adaptive rating gauge.</h1>

      {phase === "intro" && (
        <div className="card-panel p-8 max-w-xl" data-testid="elo-intro">
          <p className="text-[#A8A196] mb-4 leading-relaxed">
            Eight puzzles. Each answer bumps our estimate of your rating up or down — the same adaptive method the big
            chess sites use — until we converge on a real number.
          </p>
          <p className="text-[#A8A196] mb-6 leading-relaxed">
            Take your time; there's no timer.
          </p>
          {err && <p className="text-sm text-[#A04A3C] mb-4" data-testid="elo-error">{err}</p>}
          <button data-testid="elo-start" onClick={start} className="btn-brass">Begin test <ArrowRight size={14} /></button>
        </div>
      )}

      {phase === "playing" && chess && current && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8">
            <Board
              position={chess.fen()}
              onPieceDrop={clickToMove.onPieceDrop}
              onSquareClick={clickToMove.onSquareClick}
              boardOrientation={orientation}
              arePiecesDraggable={!attempted}
              customSquareStyles={combinedStyles}
            />
          </div>
          <div className="lg:col-span-4 space-y-4">
            <div className="card-panel p-6">
              <div className="overline mb-3">Progress</div>
              <div className="font-serif text-4xl text-[#EFE8D8]" data-testid="elo-progress">
                {current.step} / {current.total}
              </div>
              <div className="mt-4 h-1.5 w-full bg-[#25211B] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#C6A15B] transition-all"
                  style={{ width: `${(current.step / current.total) * 100}%` }}
                />
              </div>
              <p className="text-sm text-[#A8A196] mt-4">
                Current estimate: <span className="font-mono text-[#C6A15B]" data-testid="elo-current-estimate">{current.current_estimate}</span>
              </p>
              <p className="text-xs text-[#756F67] mt-1 font-mono">
                Puzzle rating: {current.puzzle.rating}
              </p>
              <p className="text-xs text-[#756F67] mt-4 font-mono">Playing as {orientation}</p>
            </div>
          </div>
        </div>
      )}

      {phase === "done" && finalRes && (
        <div className="card-panel p-8 max-w-xl" data-testid="elo-result">
          <Trophy size={28} className="text-[#C6A15B] mb-4" />
          <div className="overline mb-3">Your estimate</div>
          <div className="font-serif text-6xl text-[#C6A15B] mb-2" data-testid="elo-final-rating">
            {finalRes.elo_estimate}
          </div>
          <div className="font-serif text-2xl text-[#EFE8D8] mb-1" data-testid="elo-band">{finalRes.band}</div>
          <div className="text-sm font-mono text-[#A8A196] mb-6" data-testid="elo-percentile">
            You're in {finalRes.percentile_text} of rated online players.
          </div>
          <p className="text-[#EFE8D8] leading-relaxed mb-6" data-testid="elo-message">
            {finalRes.message}
          </p>
          <p className="text-xs text-[#756F67] font-mono mb-6">
            Solved {finalRes.solved} of {finalRes.total}.
          </p>
          <div className="flex gap-3">
            <Link to="/puzzles" className="btn-brass">Keep training <ArrowRight size={14} /></Link>
            <button onClick={() => setPhase("intro")} className="btn-ghost">Test again</button>
          </div>
        </div>
      )}
    </div>
  );
}
